import { useEffect, useState } from "react";
import {
  ArrowDown, ArrowUpRight, AudioLines, BedDouble, BusFront, Check,
  Copy, Drum, Guitar, MicVocal, Music, Utensils,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Layout } from "./components/layout";
import { Header } from "./components/header";
import { Gallery } from "./components/gallery";
import { BandHero } from "./components/band-hero";
import { Shows } from "./components/shows";
import { useScrollFadeIn } from "./hooks/useScrollFadeIn";
import { instruments, members, tour, type Instrument } from "./lib/tour";

const roleIcons: Record<Instrument, LucideIcon> = {
  Vocals: MicVocal,
  Guitar,
  Drums: Drum,
  Bass: Music,
};

function App() {
  const [instrument, setInstrument] = useState<"All" | Instrument>("All");
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const { ref: bandRef, isVisible: bandVisible } = useScrollFadeIn();
  const visibleMembers = members.filter((member) => instrument === "All" || member.role === instrument);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "instant" });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (copyStatus !== "copied") return;
    const timer = setTimeout(() => setCopyStatus("idle"), 3000);
    return () => clearTimeout(timer);
  }, [copyStatus]);

  const copyFundraiser = async () => {
    if (!navigator.clipboard) {
      setCopyStatus("error");
      return;
    }
    try {
      await navigator.clipboard.writeText(tour.fundraiserUrl);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  return (
    <Layout>
      <a className="skip-link" href="#main">Skip to content</a>
      <Header />
      <main id="main" tabIndex={-1}>
        <BandHero />

        <section id="band" className="section shell" aria-labelledby="band-title">
          <div ref={bandRef} className={`section-reveal ${bandVisible ? "visible" : ""}`}>
            <div className="section-heading">
              <div>
                <p className="eyebrow"><span>01</span> / The {tour.year} lineup</p>
                <h2 id="band-title">Meet the<br /><em>rock stars.</em></h2>
              </div>
              <p className="section-intro">
                {members.length} young musicians from
                School of Rock Lakewood Ranch, finding their sound together.
              </p>
            </div>

            <div className="lineup-toolbar">
              <div className="filter-group" role="group" aria-label="Filter band members by instrument">
                {(["All", ...instruments] as const).map((role) => {
                  const count = role === "All" ? members.length : members.filter((member) => member.role === role).length;
                  const label = role === "All" ? "Full band" : role;
                  return (
                    <button
                      key={role}
                      type="button"
                      className="filter-button"
                      aria-label={`${label}, ${count} musicians`}
                      aria-pressed={instrument === role}
                      aria-controls="band-members"
                      onClick={() => setInstrument(role)}
                    >
                      {label}<span aria-hidden="true">{count}</span>
                    </button>
                  );
                })}
              </div>
              <p className="result-count" role="status">
                {visibleMembers.length} {visibleMembers.length === 1 ? "musician" : "musicians"}
              </p>
            </div>

            <ul id="band-members" className="member-grid">
              {visibleMembers.map((member) => {
                const Icon = roleIcons[member.role];
                return (
                  <li key={member.name} className="member-card">
                    <div className="member-card-top">
                      <span className="member-number">{String(members.indexOf(member) + 1).padStart(2, "0")}</span>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{member.name}</h3>
                    <p>{member.role}</p>
                  </li>
                );
              })}
              {instrument === "All" && (
                <li className="band-note" role="presentation">
                  <AudioLines aria-hidden="true" />
                  <div><strong>Different instruments.<br />One House Band.</strong><p>Lakewood Ranch, Florida</p></div>
                </li>
              )}
            </ul>
          </div>
        </section>

        <Shows />
        <Gallery />

        <section id="support" className="support-section" aria-labelledby="support-title">
          <div className="shell support-grid">
            <div className="support-story">
              <p className="eyebrow"><span>04</span> / Be part of the tour</p>
              <h2 id="support-title">You help us<br />go places.</h2>
              <p>
                A summer on the road means more than a few great shows.
                It's a chance for these young musicians to perform, grow,
                and make memories together.
              </p>
              <p>
                Your support helps our Lakewood Ranch House Band get to the
                next stage. Every donation, and every share, makes a difference.
              </p>
              <ul className="tour-expenses" aria-label="What donations support">
                <li><BusFront aria-hidden="true" /><span>Travel</span></li>
                <li><BedDouble aria-hidden="true" /><span>Lodging</span></li>
                <li><Utensils aria-hidden="true" /><span>Meals</span></li>
              </ul>
              <a className="sponsor-link" href={`mailto:${tour.bookingEmail}`}>
                Booking or sponsorship inquiries <ArrowUpRight aria-hidden="true" />
              </a>
            </div>

            <div className="fundraiser-ticket">
              <div className="ticket-topline">
                <span className="eyebrow">Back the band</span>
                <ArrowUpRight aria-hidden="true" />
              </div>
              <p className="ticket-year">{tour.year}</p>
              <h3>Small donations.<br />Big possibilities.</h3>
              <p>Help cover travel, lodging, meals, and other summer tour expenses.</p>
              <a className="button button-primary" href={tour.fundraiserUrl} target="_blank" rel="noopener noreferrer">
                Donate on GoFundMe <ArrowUpRight aria-hidden="true" />
              </a>
              <button type="button" className="button button-secondary copy-button" onClick={copyFundraiser}>
                {copyStatus === "copied" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                {copyStatus === "copied" ? "Link copied" : "Copy fundraiser link"}
              </button>
              <div role="status" className="copy-status">
                {copyStatus === "copied" && "Ready to share. Thank you for spreading the word."}
                {copyStatus === "error" && (
                  <>Couldn't copy automatically. Copy this link instead: <a href={tour.fundraiserUrl}>{tour.fundraiserUrl}</a></>
                )}
              </div>
              <p className="fundraiser-note">Current totals and campaign updates are on GoFundMe.</p>
              <div className="ticket-bottomline"><span>Lakewood Ranch, FL</span><AudioLines aria-hidden="true" /><span>Summer tour</span></div>
            </div>
          </div>
        </section>

        <div className="closing-note shell">
          <span>See you in the crowd.</span>
          <a href="#home">Back to the top <ArrowDown aria-hidden="true" /></a>
        </div>
      </main>
    </Layout>
  );
}

export default App;
